import type { MouseEvent } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { Form } from '../types/schema';
import type { tGroupedEquipment } from '../types';
import { LOCAL_STORAGE_KEYS, FORM_TYPES } from './constants';
import { resolveFormType } from './reportUtils';

function isPublicStorageImagePath(imagePath?: string | null): imagePath is string {
    return Boolean(imagePath && imagePath.split('/')[0] === 'public');
}

/** Max favorites a user can save (Forms page + MultiSelect). */
const MAX_FORM_FAVORITES = 10;

/** How many favorites Home shows as report cards (first N in order). */
const MAX_HOME_DISPLAY_FAVORITES = 4;

export function getHomeFavoriteFormsStorageKey(companyId?: string | null, userId?: string | null): string | null {
    if (!companyId || !userId) {
        return null;
    }

    return `${LOCAL_STORAGE_KEYS.HOME_FAVORITE_FORM_IDS}:${companyId}:${userId}`;
}

/** True when the user has an explicit saved selection (including an empty list). */
export function hasStoredHomeFavoriteFormIds(companyId?: string | null, userId?: string | null): boolean {
    const key = getHomeFavoriteFormsStorageKey(companyId, userId);
    if (!key) {
        return false;
    }

    return localStorage.getItem(key) !== null;
}

export function readHomeFavoriteFormIds(companyId?: string | null, userId?: string | null): string[] {
    const key = getHomeFavoriteFormsStorageKey(companyId, userId);
    if (!key) {
        return [];
    }

    try {
        const raw = localStorage.getItem(key);
        if (raw === null) {
            return [];
        }

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
    } catch {
        return [];
    }
}

/**
 * Returns stored favorites when present; otherwise the provided defaults
 * (e.g. Start of Day + End of Day) capped at MAX_FORM_FAVORITES.
 */
export function resolveHomeFavoriteFormIds(
    companyId: string | null | undefined,
    userId: string | null | undefined,
    defaultFormIds: string[],
): string[] {
    if (hasStoredHomeFavoriteFormIds(companyId, userId)) {
        return readHomeFavoriteFormIds(companyId, userId).slice(0, MAX_FORM_FAVORITES);
    }

    return Array.from(new Set(defaultFormIds.filter(Boolean))).slice(0, MAX_FORM_FAVORITES);
}

/** First N favorites for Home report cards. */
export function getHomeDisplayFavoriteFormIds(formIds: string[]): string[] {
    return formIds.slice(0, MAX_HOME_DISPLAY_FAVORITES);
}

export function writeHomeFavoriteFormIds(
    formIds: string[],
    companyId?: string | null,
    userId?: string | null,
): string[] {
    const key = getHomeFavoriteFormsStorageKey(companyId, userId);
    const next = Array.from(new Set(formIds)).slice(0, MAX_FORM_FAVORITES);

    if (!key) {
        return next;
    }

    localStorage.setItem(key, JSON.stringify(next));
    return next;
}

export function toggleHomeFavoriteFormId(
    formId: string,
    companyId?: string | null,
    userId?: string | null,
    defaultFormIds: string[] = [],
): string[] {
    const current = resolveHomeFavoriteFormIds(companyId, userId, defaultFormIds);

    if (current.includes(formId)) {
        return writeHomeFavoriteFormIds(
            current.filter((id) => id !== formId),
            companyId,
            userId,
        );
    }

    if (current.length >= MAX_FORM_FAVORITES) {
        return current;
    }

    return writeHomeFavoriteFormIds([...current, formId], companyId, userId);
}

/** Move a favorited form one step up (-1) or down (+1) in the shared order. */
export function moveHomeFavoriteFormId(
    formId: string,
    direction: -1 | 1,
    companyId?: string | null,
    userId?: string | null,
    defaultFormIds: string[] = [],
): string[] {
    const current = resolveHomeFavoriteFormIds(companyId, userId, defaultFormIds);
    const fromIndex = current.indexOf(formId);

    if (fromIndex < 0) {
        return current;
    }

    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= current.length) {
        return current;
    }

    const next = [...current];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    return writeHomeFavoriteFormIds(next, companyId, userId);
}

/** Reorder favorites after drag-and-drop (active row dropped onto over row). */
export function reorderHomeFavoriteFormIds(
    activeId: string,
    overId: string,
    companyId?: string | null,
    userId?: string | null,
    defaultFormIds: string[] = [],
): string[] {
    const current = resolveHomeFavoriteFormIds(companyId, userId, defaultFormIds);
    const fromIndex = current.indexOf(activeId);
    const toIndex = current.indexOf(overId);

    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return current;
    }

    const next = [...current];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    return writeHomeFavoriteFormIds(next, companyId, userId);
}

export { MAX_FORM_FAVORITES, MAX_HOME_DISPLAY_FAVORITES };

/** @deprecated Use MAX_FORM_FAVORITES / MAX_HOME_DISPLAY_FAVORITES */
export const MAX_HOME_FORM_FAVORITES = MAX_FORM_FAVORITES;

type FormTypeFields = Pick<
    Form,
    'id' | 'formType' | 'inspectionForm' | 'dailyServiceLogForm' | 'pmServiceScheduleForm' | 'name'
>;

/** Equipment categories (already assignment-scoped) that use this favorited form. */
export function filterEquipmentForHomeFavoriteForm(
    scopedEquipment: tGroupedEquipment[],
    form: FormTypeFields,
): tGroupedEquipment[] {
    return scopedEquipment.filter((equipment) => equipmentMatchesHomeFavoriteForm(equipment, form));
}

/** Whether equipment (category + units) is assigned to a favorited form. */
export function equipmentMatchesHomeFavoriteForm(equipment: tGroupedEquipment, form: FormTypeFields): boolean {
    const formId = form.id;
    if (!formId) {
        return false;
    }

    const formType = resolveFormType(form);

    if (formType === FORM_TYPES.DAILY_SERVICE) {
        if (equipment.dailyServiceLogFormId === formId) {
            return true;
        }

        return equipment.units?.some((unit) => unit?.dailyServiceLogFormId === formId) ?? false;
    }

    return equipment.formId === formId;
}

/** Short label under home report cards (non–Start/End-of-day). */
export function getHomeFavoriteFormMetaLabel(form: FormTypeFields): string {
    const formType = resolveFormType(form);

    if (formType === FORM_TYPES.DAILY_SERVICE) {
        return 'Daily service form';
    }

    if (formType === FORM_TYPES.SHIFT_REPORT) {
        return 'Shift report';
    }

    return 'Inspection form';
}

/** Cache key for resolved category thumbnails on home favorite cards. */
export function getHomeFavoriteFormImageKey(formId: string): string {
    return `favorite-form:${formId}`;
}

/**
 * First **equipment category** photo (company-wide) for categories assigned to this form.
 * Does not use user assignment scope or unit-level photos.
 */
export function findHomeFavoriteFormCategoryImage(
    companyEquipment: tGroupedEquipment[],
    form: FormTypeFields,
): { imageKey: string; imagePath: string } | null {
    const formId = form.id;
    if (!formId) {
        return null;
    }

    const imageKey = getHomeFavoriteFormImageKey(formId);

    for (const equipment of companyEquipment) {
        if (!equipmentMatchesHomeFavoriteForm(equipment, form)) {
            continue;
        }

        if (isPublicStorageImagePath(equipment.image)) {
            return { imageKey, imagePath: equipment.image };
        }
    }

    return null;
}

/** @deprecated Use findHomeFavoriteFormCategoryImage */
export const findRepresentativeImageForHomeFavoriteForm = findHomeFavoriteFormCategoryImage;

/** `/inspect` query when launching from a filtered home equipment tile. */
export function getHomeEquipmentInspectHref(equipmentId: string, activeForm: FormTypeFields | null | undefined): string {
    const base = `/inspect/${equipmentId}`;

    if (activeForm && resolveFormType(activeForm) === FORM_TYPES.DAILY_SERVICE) {
        return `${base}?action=dailyService`;
    }

    return base;
}

/**
 * Primary click: in-app `navigate` (keeps Home in history for back).
 * Modifier or non-primary button: native `<a href>` behavior (new tab/window for multitaskers).
 */
export function handleHomeEquipmentInspectClick(
    event: MouseEvent<HTMLAnchorElement>,
    inspectHref: string,
    navigate: NavigateFunction,
): void {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
    ) {
        return;
    }

    event.preventDefault();
    navigate(inspectHref);
}
